import { useEffect, useMemo } from 'react';
import { useGlobalState } from '../../stateProvider/StateProvider';
import RoleProtected, { userRoles } from '../../RoleProtected';
import HamQuest from '../../api/HamQuestGiver';
import { QuestRoute } from '../../api/QuestRoutes';
import Button from '../../forms/Button';
import Input from "../../forms/Input";
import Select from "../../forms/Select";
import FormSetting from "../../layout/FormSetting";
import PageBreak from "../../layout/PageBreak";
import Row from '../../layout/Row';
import Col from '../../layout/Col';
import styled from 'styled-components';
import Heading from '../../forms/Heading';
import Checkbox from '../../forms/Checkbox';
import RequireFeature, { Features } from '../../RequireFeature';
import Preput from '../../forms/Preput';
import GetState from '../../GetState';

const NoConfigsMessage = styled.i`
    color: ${({ theme }) => theme.gray};
    font-weight: 500;
    font-size: 0.8em;
`;

const Label = styled.div`
    font-weight: bold;
    font-size: 0.8em;
    margin-bottom: -0.5em;
`;

const SettingsGroupWrapper = styled(Row)`
    display: grid;
`;

const FieldGroupWrapper = styled.div`
    display: grid;
    padding: 0.5em;
    border: 1px solid ${({ theme }) => theme.gray};
    border-radius: 0.4em;
    row-gap: 0.2em;
`;

const UpsellAwardGroup = styled(Row)`
    border-bottom: 1px solid ${({ theme }) => theme.gray};
    margin-bottom: 0.5rem;
    padding-bottom: 0.5rem;

    &:last-child {
        border-bottom: none;
        margin-bottom: 0;
        padding-bottom: 0;
    }
`;

const LocationExceptionsEditor = ({
    values,
    setValues,
    editing,
    setErrors,
    resetErrors,
    locations
}) => {
    const configs = values.locationRetentionConfigs || [];

    const locDropdown = useMemo(
        () =>
            locations
                .filter(l => !configs.map(i => i.locationId).includes(l.value))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [locations, configs]
    );

    const getLocationName = locationId =>
        locations.find(l => l.value === locationId)?.label || "Unknown Location";

    const updateConfigs = updater => {
        setValues(v => ({
            ...v,
            locationRetentionConfigs: updater(v.locationRetentionConfigs || [])
        }));
    };

    const handleLocChange = (locationId, value) => {
        const retentionPeriod = value === '' ? null : parseInt(value, 10);

        updateConfigs(prev =>
            prev.map(item =>
                item.locationId === locationId
                    ? { ...item, retentionPeriod }
                    : item
            )
        );
    };

    const removeRow = index => {
        updateConfigs(prev => prev.toSpliced(index, 1));
        resetErrors();      
    };

    const addRow = e => {
        const locationId = parseInt(e.target.value, 10);
        if (!locationId) return;

        const locationName = e.target.selectedOptions[0]?.text || "Unknown Location";

        updateConfigs(prev => [
            ...prev,
            {
                locationId,
                locationName,
                retentionPeriod: null
            }
        ]);
    };

    return (
        <FieldGroupWrapper>
            {configs.length === 0 && (
                <NoConfigsMessage>
                    <div>No location exceptions configured.</div>
                    <div>Default retention period will be used.</div>
                </NoConfigsMessage>
            )}

            {configs.map((loc, index) => (
                <Row key={loc.locationId}>
                    <Col style={{ width: "16vw" }}>
                        <Input
                            value={getLocationName(loc.locationId)}
                            disabled
                            required
                        />
                    </Col>

                    <Col style={{ width: "10vw" }}>
                        <Input
                            type="number"
                            value={loc.retentionPeriod ?? ''}
                            onChange={e =>
                                handleLocChange(loc.locationId, e.target.value)
                            }
                            required
                        />
                    </Col>

                    {!editing && (
                        <Col>
                            <Button onClick={() => removeRow(index)}>Remove</Button>
                        </Col>
                    )}
                </Row>
            ))}

            {!editing && (
                <Row wrap>
                    <Col style={{ width: "16vw" }}>
                        <Select
                            label="Add Location Exception"
                            name="addLocationException"
                            options={locDropdown}
                            nullable
                            onChange={addRow}
                        />
                    </Col>
                </Row>
            )}
        </FieldGroupWrapper>
    );
};

const NewVisitorAwardsConfigEditor = ({
    values,
    setValues,
    editing,
    setErrors,
    resetErrors,
    awardOptions,
    messageOptions
}) => {
    const configs = values.newVisitorAwardConfigs || [];

    const updateConfigs = updater => {
        setValues(v => ({
            ...v,
            newVisitorAwardConfigs: updater(v.newVisitorAwardConfigs || [])
        }));
    };

    const validateRules = updated => {
        resetErrors();

        const errors = {};

        // Rule 1: If enabled, Visit #1 must exist
        if (values.newVisitorAwardsEnabled) {
            const hasVisit1 = updated.some(c => c.visitNumber === 1);
            if (!hasVisit1) {
                errors.Visit1Required = true;
            }
        }

        // Rule 2: Must be sequential with no gaps
        const visitNumbers = updated.map(c => c.visitNumber).sort((a, b) => a - b);

        for (let i = 0; i < visitNumbers.length; i++) {
            if (visitNumbers[i] !== i + 1) {
                errors.SequentialVisits = true;
                break;
            }
        }

        // Rule 3: Max 5 visits
        if (visitNumbers.length > 5) {
            errors.MaxVisits = true;
        }

        setErrors(errors);
    };

    const handleConfigChange = (index, patch) => {
        const updated = configs.map((c, i) =>
            i === index ? { ...c, ...patch } : c
        );
        updateConfigs(() => updated);
        validateRules(updated);
    };

    const addRow = () => {
        const nextVisitNumber = configs.length + 1;

        if (nextVisitNumber > 5) return;

        const newRow = {
            visitNumber: nextVisitNumber,
            awardId: null,
            message: null,
            priority: 3,
            expiresDays: 30
        };

        const updated = [...configs, newRow];
        updateConfigs(() => updated);
        validateRules(updated);
    };

    const removeRow = index => {
        const updated = configs.toSpliced(index, 1);

        // Reindex visit numbers sequentially
        const reindexed = updated.map((c, i) => ({
            ...c,
            visitNumber: i + 1
        }));

        updateConfigs(() => reindexed);
        validateRules(reindexed);
    };


    return (
        <FieldGroupWrapper>
            {configs.length === 0 && (
                <NoConfigsMessage>No New Visitor Award configurations defined.</NoConfigsMessage>
            )}

            {configs.map((cfg, index) => (
                <UpsellAwardGroup key={index} wrap style={{ alignItems: "flex-start" }}>
                    <Col style={{ width: "10vw" }}>
                        <Input
                            name={`VisitNumber_${index}`}
                            label="Visit Number"
                            type="number"
                            min="1"
                            max="5"
                            value={cfg.visitNumber ?? 1}
                            required
                            disabled
                        />
                    </Col>

                    <Col style={{ width: "14vw" }}>
                        <Select
                            name={`Award_${index}`}
                            label="Award"
                            nullable
                            options={awardOptions}
                            value={cfg.awardId ?? ''}
                            callback={val =>
                                handleConfigChange(index, {
                                    awardId: val === '' ? null : parseInt(val, 10)
                                })
                            }
                        />
                    </Col>
                    {
                        index > 0 && (
                            <>
                                <Col style={{ width: "31vw"}}>
                                    <Input
                                        label="Priority"
                                        type="number"
                                        value={cfg.priority ?? 3}
                                        min="1"                         
                                        onChange={e =>
                                            handleConfigChange(index, {
                                                priority: parseInt(e.target.value)
                                            })
                                        }
                                        required
                                    />
                                </Col>
                                <Col style={{ width: "31vw"}}>
                                    <Input
                                        label="Expires (Days)"
                                        type="number"
                                        value={cfg.expiresDays ?? 30}
                                        min="1"                         
                                        onChange={e =>
                                            handleConfigChange(index, {
                                                expiresDays: parseInt(e.target.value)
                                            })
                                        }
                                        required
                                    />
                                </Col>                              
                            </>
                        )
                    }

                    <Col style={{ width: "31vw", marginTop: "0.5rem" }}>
                        <Preput
                            label="Kiosk Display Message"
                            value={cfg.message ?? ''}
                            onChange={e =>
                                handleConfigChange(index, {
                                    message: e.target.value
                                })
                            }
                            required
                        />
                    </Col>
                    {!editing && (
                        <Col style={{ marginLeft: "auto" }}>
                            <Button onClick={() => removeRow(index)}>Remove</Button>
                        </Col>
                    )}
                </UpsellAwardGroup>
            ))}

            {!editing && configs.length < 5 && (
                <Row wrap>
                    <Col style={{ width: "16vw" }}>
                        <Button onClick={addRow}>Add Visit Award</Button>
                    </Col>
                </Row>
            )}
        </FieldGroupWrapper>
    );
};

export const UpsellConfigEditor = ({
    values,
    setValues,
    editing,
    setErrors,
    resetErrors,
    kioskItemOptions,
    awardOptions
}) => {
    const configs = values.upsellAwardConfigs || [];
    const usedIds = useMemo(
        () => configs.map(c => c.kioskItemId),
        [configs]
    );

    const availableKioskOptions = useMemo(
        () => kioskItemOptions.filter(opt => !usedIds.includes(opt.value)),
        [kioskItemOptions, usedIds]
    );

    const getKioskName = id =>
        kioskItemOptions.find(k => k.value === id)?.label || "Unknown Item";

    const updateConfigs = updater => {
        setValues(v => ({
            ...v,
            upsellAwardConfigs: updater(v.upsellAwardConfigs || [])
        }));
    };

    const handleConfigChange = (index, patch) => {
        updateConfigs(prev =>
            prev.map((c, i) =>
                i === index ? { ...c, ...patch } : c
            )
        );
    };

    const addRow = kioskItemId => {
        if (!kioskItemId) return;

        const newRow = {
            kioskItemId,
            awardId: null,
            message: null,
            priority: 3,
            intervalDays: 1,
            expiresDays: 30
        };

        updateConfigs(prev => [...prev, newRow]);
    };

    const removeRow = index => {
        updateConfigs(prev => prev.toSpliced(index, 1));
    };

    return (
        <FieldGroupWrapper>
            {configs.length === 0 && (
                <NoConfigsMessage>No upsell configurations defined.</NoConfigsMessage>
            )}

            {configs.map((cfg, index) => (
                <UpsellAwardGroup key={index} wrap style={{ alignItems: "flex-start" }}>
                    <Col style={{ width: "16vw" }}>
                        <Input
                            name={`UpsellItem_${cfg.kioskItemId}`}
                            label="Purchased Item"
                            value={getKioskName(cfg.kioskItemId)}
                            required
                            disabled
                        />
                    </Col>

                    <Col style={{ width: "14vw" }}>
                        <Select
                            name={`UpsellAwardForItem_${cfg.kioskItemId}`}
                            label="Award"
                            nullable
                            options={awardOptions}
                            value={cfg.awardId ?? ''}
                            callback={val =>
                                handleConfigChange(index, {
                                    awardId: val === '' ? null : parseInt(val, 10)
                                })
                            }
                        />
                    </Col>

                    {!editing && (
                        <Col style={{ marginLeft: "auto" }}>
                            <Button onClick={() => removeRow(index)}>Remove</Button>
                        </Col>
                    )}
                    <Col style={{ width: "31vw"}}>
                        <Input
                            label="Priority"
                            type="number"
                            value={cfg.priority ?? 3}
                            min="1"                         
                            onChange={e =>
                                handleConfigChange(index, {
                                    priority: parseInt(e.target.value)
                                })
                            }
                            required
                        />
                    </Col>
                    <Col style={{ width: "31vw"}}>
                        <Input
                            label="Minimum Award Interval (Days)"
                            type="number"
                            value={cfg.intervalDays ?? 1}
                            min="1"                         
                            onChange={e =>
                                handleConfigChange(index, {
                                    intervalDays: parseInt(e.target.value)
                                })
                            }
                            required
                        />
                    </Col>
                    <Col style={{ width: "31vw"}}>
                        <Input
                            label="Expires (Days)"
                            type="number"
                            value={cfg.expiresDays ?? 30}
                            min="1"                         
                            onChange={e =>
                                handleConfigChange(index, {
                                    expiresDays: parseInt(e.target.value)
                                })
                            }
                            required
                        />
                    </Col>                    
                    <Col style={{ width: "31vw", marginTop: "0.5rem" }}>
                        <Preput
                            label="Kiosk Display Message"
                            value={cfg.message ?? ''}
                            onChange={e =>
                                handleConfigChange(index, {
                                    message: e.target.value
                                })
                            }
                            required 
                        />
                    </Col>
                </UpsellAwardGroup>
            ))}

            {!editing && kioskItemOptions.length > 0 && (
                <Row wrap>
                    <Col style={{ width: "16vw" }}>
                        <Select
                            label="Select a Purchased Item"
                            name="addUpsellConfig"
                            options={availableKioskOptions}
                            nullable
                            onChange={e => addRow(parseInt(e.target.value, 10))}
                        />
                    </Col>
                </Row>
            )}
        </FieldGroupWrapper>
    );
};

const LPRSettingsForm = ({
    values,
    setValues,
    editing,
    setErrors,
    resetErrors,
    locations,
    kioskItemOptions,
    awardOptions
}) => {
    return (
        <PageBreak>
            <SettingsGroupWrapper>
                <Heading>Plate Read Data Retention</Heading>

                <Row wrap>
                    <Col style={{ width: '26vw' }}>
                        <Input
                            label='Default Retention Period (Days)'
                            name='defaultRetentionPeriod'
                            type='number'
                            required
                        />
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Label>Location Exceptions</Label>
                        <LocationExceptionsEditor
                            values={values}
                            setValues={setValues}
                            editing={editing}
                            setErrors={setErrors}
                            resetErrors={resetErrors}
                            locations={locations}
                        />
                    </Col>
                </Row>
            </SettingsGroupWrapper>

            <SettingsGroupWrapper>
                <Heading>Vended Wash Repayments</Heading>
                <Row>
                    <Col style={{ width: '14vw' }}>
                        <Checkbox label='Add Sales Tax' name='taxWashRepayments' />
                    </Col>
                </Row>
            </SettingsGroupWrapper>
        </PageBreak>
    );
};

const AutoAwardsForm = ({
    values,
    setValues,
    editing,
    setErrors,
    resetErrors,
    kioskItemOptions,
    awardOptions
}) => {
    useEffect(() => {
        if (values.nthVisitAwardEnabled) {
            setValues(v => ({
                ...v,
                nthVisitAwardPriority: v.nthVisitAwardPriority ?? 3,
                nthVisitExpiresDays: v.nthVisitExpiresDays ?? 30
            }));
        }
    }, [values.nthVisitAwardEnabled, setValues]);

    return (
        <PageBreak>
            <SettingsGroupWrapper>
                <Heading>New Visitor Awards</Heading>
                <Row>
                    <Col style={{ width: '14vw' }}>
                        <Checkbox label='Enabled' name='newVisitorAwardsEnabled' />
                    </Col>
                </Row>
                {values.newVisitorAwardsEnabled && (
                    <NewVisitorAwardsConfigEditor
                        values={values}
                        setValues={setValues}
                        editing={editing}
                        setErrors={setErrors}
                        resetErrors={resetErrors}
                        kioskItemOptions={kioskItemOptions}
                        awardOptions={awardOptions}
                    />
                )}
            </SettingsGroupWrapper>

            <SettingsGroupWrapper>
                <Heading>We Missed You Award</Heading>
                <Row>
                    <Col style={{ width: '14vw' }}>
                        <Checkbox label='Enabled' name='weMissedYouAwardEnabled' />
                    </Col>
                </Row>
                {values.weMissedYouAwardEnabled && (
                    <>
                        <Row>
                            <Col style={{ width: '10vw' }}>
                                <Input label='Days Since Last Visit' name='weMissedYouDays' min='1' type='number' required />
                            </Col>
                            <Col style={{ width: '18vw' }}>
                                <Select
                                    label='Award'
                                    name='weMissedYouAwardId'
                                    nullable
                                    options={awardOptions}
                                    buildOptions={e => {
                                        e.label = e.name;
                                        e.value = e.awardId;
                                    }}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col style={{ width: '18vw' }}>
                                <Input label='Priority' name='weMissedYouAwardPriority' min='1' value={values.weMissedYouAwardPriority ?? 3} type='number' required />
                            </Col>
                        </Row>
                        <Row>
                            <Col style={{ width: '30vw' }}>
                                <Preput label='Kiosk Display Message' name='weMissedYouAwardMessage' />
                            </Col>
                        </Row>
                    </>
                )}
            </SettingsGroupWrapper>

            <SettingsGroupWrapper>
                <Heading>Nth Visit Award</Heading>
                <Row>
                    <Col style={{ width: '14vw' }}>
                        <Checkbox label='Enabled' name='nthVisitAwardEnabled' />
                    </Col>
                </Row>
                {values.nthVisitAwardEnabled && (
                    <>
                        <Row>
                            <Col style={{ width: '10vw' }}>
                                <Input label='Required Visits' name='nthVisitCount' min='1' type='number' required />
                            </Col>
                            <Col style={{ width: '18vw' }}>
                                <Select
                                    label='Award'
                                    name='nthVisitAwardId'
                                    nullable
                                    options={awardOptions}
                                    buildOptions={e => {
                                        e.label = e.name;
                                        e.value = e.awardId;
                                    }}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col style={{ width: '18vw' }}>
                                <Input label='Priority' name='nthVisitAwardPriority' min='1' defaultValue={values.nthVisitAwardPriority ?? 3} type='number' required />
                            </Col>
                            <Col style={{ width: '18vw' }}>
                                <Input label='Expires (Days)' name='nthVisitExpiresDays' min='1' defaultValue={values.nthVisitExpiresDays ?? 30} type='number' required />
                            </Col>
                        </Row>
                        <Row>
                            <Col style={{ width: '30vw' }}>
                                <Preput label='Kiosk Display Message' name='nthVisitAwardMessage' required />
                            </Col>
                        </Row>
                    </>
                )}
            </SettingsGroupWrapper>

            <SettingsGroupWrapper>
                <Heading>Upsell Awards</Heading>
                <Row>
                    <Col style={{ width: '14vw' }}>
                        <Checkbox label='Enabled' name='upsellAwardsEnabled' />
                    </Col>
                </Row>
                {values.upsellAwardsEnabled && (
                    <UpsellConfigEditor
                        values={values}
                        setValues={setValues}
                        editing={editing}
                        setErrors={setErrors}
                        resetErrors={resetErrors}
                        kioskItemOptions={kioskItemOptions}
                        awardOptions={awardOptions}
                    />
                )}
            </SettingsGroupWrapper>
        </PageBreak>
    );
};

const LPRSettings = () => {
    const { state: { locations } } = useGlobalState();
    const [kioskItemsRaw] = GetState(QuestRoute.kiosks.wash);
    const kioskItems = Array.isArray(kioskItemsRaw) ? kioskItemsRaw : [];

    const [availableAwardsRaw] = GetState(QuestRoute.plateReads.plateAwards);
    const availableAwards = Array.isArray(availableAwardsRaw) ? availableAwardsRaw : [];

    const KioskItemOptions = useMemo(() => {
        return kioskItems
            .map(k => ({ label: k.name, value: k.id }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [kioskItems]);

    const AwardOptions = useMemo(() => {
        return (availableAwards || []).map(a => ({
            label: a.name,
            value: a.awardId
        }));
    }, [availableAwards]);

    return (
        <RequireFeature requiredFeatures={Features.PlateReads}>
            <PageBreak>
                <RoleProtected roles={userRoles.admin}>
                    <FormSetting
                        label='LPR Settings'
                        name='lprSettingsDataRetention'
                        getInputs={QuestRoute.plateReads.settings}
                        onSubmit={ e => HamQuest.put(QuestRoute.plateReads.settings, { ...e }) }
                        text='Update'
                        editable
                    >
                        {({ values, setValues, editing, setErrors, resetErrors }) => (
                            <LPRSettingsForm
                                values={values}
                                setValues={setValues}
                                editing={editing}
                                setErrors={setErrors}
                                resetErrors={resetErrors}
                                locations={locations}
                                kioskItemOptions={KioskItemOptions}
                                awardOptions={AwardOptions}
                            />
                        )}
                    </FormSetting>
                </RoleProtected>
            </PageBreak>
        </RequireFeature>
    );
};

export const AutoAwards = () => {
    const { state: { locations } } = useGlobalState();
    const [kioskItemsRaw] = GetState(QuestRoute.kiosks.wash);
    const kioskItems = Array.isArray(kioskItemsRaw) ? kioskItemsRaw : [];
    const [availableAwardsRaw] = GetState(QuestRoute.plateReads.plateAwards);
    const availableAwards = Array.isArray(availableAwardsRaw) ? availableAwardsRaw : [];

    const kioskItemOptions = useMemo(() => kioskItems
        .map(k => ({ label: k.name, value: k.id }))
        .sort((a, b) => a.label.localeCompare(b.label)), [kioskItems]);
    const awardOptions = useMemo(() => availableAwards.map(a => ({
        label: a.name,
        value: a.awardId
    })), [availableAwards]);

    return (
        <RequireFeature requiredFeatures={Features.PlateReadAutoAwards}>
            <RoleProtected roles={userRoles.admin}>
                <FormSetting
                    label='Auto Awards'
                    name='plateReadAutoAwards'
                    getInputs={QuestRoute.plateReads.settings}
                    onSubmit={e => HamQuest.put(QuestRoute.plateReads.settings, { ...e })}
                    text='Update'
                    editable
                >
                    {({ values, setValues, editing, setErrors, resetErrors }) => (
                        <AutoAwardsForm
                            values={values}
                            setValues={setValues}
                            editing={editing}
                            setErrors={setErrors}
                            resetErrors={resetErrors}
                            kioskItemOptions={kioskItemOptions}
                            awardOptions={awardOptions}
                        />
                    )}
                </FormSetting>
            </RoleProtected>
        </RequireFeature>
    );
};

export default LPRSettings;
